import React, { useState } from 'react';
import {
  Avatar,
  Button,
  TextField,
  Paper,
  Box,
  Grid,
  Typography,
  Snackbar,
  Alert,
  CssBaseline,
  useMediaQuery,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grow
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import theme from '../../theme'; // Import the shared theme


const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Extract the token from the query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  // Validate password: At least 8 characters, including a number and special character
  const validatePassword = (password) => {
    const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (!trimmedNewPassword || !trimmedConfirmPassword) {
      handleSnackbar('Please fill out all fields.', 'warning');
      return;
    }

    if (!validatePassword(trimmedNewPassword)) {
      handleSnackbar(
        'Password must be at least 8 characters long and include a number and a special character.',
        'warning'
      );
      return;
    }

    if (trimmedNewPassword !== trimmedConfirmPassword) {
      handleSnackbar('Passwords do not match.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/employees/reset-password', {
        token: token,
        newPassword: trimmedNewPassword,
      });

      if (response.status === 200) {
        handleSnackbar('Password reset successful. You can now log in.', 'success');
      } else {
        handleSnackbar('An unexpected error occurred. Please try again.', 'error');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const { message } = err.response.data;
        handleSnackbar(message || 'An error occurred. Please try again.', 'error');
      } else {
        handleSnackbar('Network error. Please check your connection.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    if (snackbarSeverity === 'success') {
      navigate('/login');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Grid
        container
        component="main"
        sx={{
          minHeight: '100vh',
          background: theme.palette.background.default,
          padding: theme.spacing(4),
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: isMobile ? theme.spacing(8) : theme.spacing(12),
        }}
      >
        {/* Grow transition for a subtle entrance animation */}
        <Grow in timeout={800}>
          <Grid
            item
            xs={10}
            sm={8}
            md={6}
            lg={4}
            component={Paper}
            elevation={4} // Reduced elevation
            sx={{
              padding: theme.spacing(4),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Avatar
                sx={{
                  m: 1,
                  bgcolor: 'primary.main',
                  width: 64,
                  height: 64,
                }}
              >
                <VpnKeyIcon fontSize="large" />
              </Avatar>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                Reset Password
              </Typography>
              <Typography
                component="p"
                variant="body2"
                color="textSecondary"
                sx={{ mb: 3, mt: 1, textAlign: 'center' }}
              >
                Please enter your new password below.
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    mt: 2,
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                </Button>
                <Grid container justifyContent="center">
                  <Grid item>
                    <Button
                      onClick={() => navigate('/login')}
                      variant="text"
                      size="small"
                    >
                      Back to Sign In
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grow>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
            elevation={6}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </ThemeProvider>
  );
};

export default ResetPasswordPage;